import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useSession } from '../auth/session';
import { brancherClavier } from './clavier';
import {
  accorderBarreEtat,
  brancherReprise,
  brancherRetourArriere,
  retirerAmorcage,
} from './coque';
import { activerNotifications, brancherNotificationsRecues } from './notifications';
import { estNatif } from './pont';

/**
 * Le raccord entre l'application et sa coque. Ne rend rien.
 *
 * Il est monté une fois, au-dessus du routeur, et il est **le seul** endroit du produit qui
 * s'adresse au téléphone : aucun écran n'importe un greffon natif, aucun n'a de branche
 * « si mobile ». C'est ce qui permet à l'application d'être la même des deux côtés, et aux
 * tests de ne monter aucune coque.
 *
 * Hors coque, chaque fonction appelée ici renonce d'elle-même : il n'y a pas de garde à
 * écrire ici, et pas de rendu conditionnel qui ferait diverger les deux arbres React.
 */
export function PontNatif() {
  const { client, profil } = useSession();
  const compte = profil?.id ?? null;
  const queryClient = useQueryClient();

  useEffect(() => {
    void retirerAmorcage();
    void accorderBarreEtat();

    /**
     * Le retour au premier plan redemande ce qui est affiché.
     *
     * Sans clé : l'invalidation porte sur toutes les familles — dépenses, journal,
     * propositions, garde, messages, alertes. Les nommer une à une ferait de cette ligne
     * une liste à tenir à jour, et la première famille ajoutée demain serait celle qui ne
     * se rafraîchirait pas. React Query, lui, ne redemande que les requêtes **montées** :
     * le coût est celui de l'écran ouvert, pas celui du produit entier.
     */
    const rafraichir = (): void => {
      void queryClient.invalidateQueries();
    };

    // Quatre raccords de même nature : posés au montage, retirés au démontage. Le clavier
    // rejoint le bouton retour ici plutôt que dans l'écran qui a un champ de saisie —
    // tous les écrans en ont un, et un écouteur posé par écran se poserait cinq fois.
    //
    // Le dernier écoute les notifications du système et redemande la même chose que le
    // retour au premier plan. Sans lui, une notification reçue **application ouverte**
    // n'aurait aucun effet : Android ne la pose pas dans le volet dans ce cas, il la remet
    // au code — et le code ne l'écoutait pas. Le parent voyait alors son co-parent écrire
    // sans qu'aucune pastille ne bouge, ce qui se lit comme une panne du produit entier.
    const arrets: (() => Promise<void>)[] = [];
    void Promise.all([
      brancherRetourArriere(),
      brancherClavier(),
      brancherReprise(rafraichir),
      brancherNotificationsRecues(rafraichir),
    ]).then((rendus) => {
      arrets.push(...rendus);
    });

    return () => {
      for (const arret of arrets) void arret();
    };
  }, [queryClient]);

  /**
   * L'enregistrement de l'appareil suit la **session**, pas le montage.
   *
   * Le système renouvelle le jeton de remise quand bon lui semble — restauration de
   * sauvegarde, réinstallation, effacement des données — et un enregistrement fait une
   * fois pour toutes finirait par ne désigner aucun appareil, sans que rien ne le signale.
   *
   * L'échec est avalé : ne pas pouvoir enregistrer l'appareil retire une commodité, cela
   * ne casse rien. Les nouveautés restent lisibles à l'écran, et l'e-mail continue de
   * partir.
   */
  useEffect(() => {
    if (!estNatif() || compte === null) return;
    void activerNotifications(client).catch(() => undefined);
  }, [client, compte]);

  return null;
}
